class LestTheme {
  constructor() {
    this.bindDrawers();
    this.bindMemberModal();
    this.bindTabs();
    this.bindQuickAdd();
    this.bindSort();
    this.bindProductExperience();
    this.bindProductVariants();
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  focusableElements(container) {
    if (!container) return [];
    return [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true');
  }

  trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const focusable = this.focusableElements(container);
    if (!focusable.length) {
      event.preventDefault();
      container?.focus?.();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  applyCouponPrice(basePrice) {
    const coupon = document.querySelector('[data-product-coupon]');
    const price = document.querySelector('[data-product-price]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    if (!coupon || !price || !basePrice) return;
    const enabled = coupon.querySelector('[data-coupon-toggle]')?.checked === true;
    const percentage = Math.max(0, Number(coupon.dataset.percent || 0));
    // Shopify allocates percentage discounts in whole currency subunits. Keep the
    // storefront preview aligned with the cart by discarding fractional cents.
    const saving = enabled ? Math.floor(Number(basePrice) * percentage / 100) : 0;
    const finalPrice = Math.max(0, Number(basePrice) - saving);
    price.dataset.basePrice = String(basePrice);
    coupon.classList.toggle('is-selected', enabled);
    const current = price.querySelector('[data-product-price-current]');
    const original = price.querySelector('[data-product-price-original]');
    const savingBadge = price.querySelector('[data-product-price-saving]');
    if (current) current.textContent = this.formatMoney(finalPrice);
    if (original) { original.textContent = this.formatMoney(basePrice); original.hidden = !enabled; }
    if (savingBadge) { savingBadge.textContent = `${coupon.dataset.savingLabel || 'Saving'} ${this.formatMoney(saving)}`; savingBadge.hidden = !enabled; }
    coupon.querySelector('[data-coupon-amount]')?.replaceChildren(document.createTextNode(this.formatMoney(saving)));
    if (stickyPrice) { stickyPrice.dataset.basePrice = String(basePrice); stickyPrice.textContent = this.formatMoney(finalPrice); }
    const stickyOriginal = document.querySelector('[data-sticky-price-original]');
    const stickySaving = document.querySelector('[data-sticky-saving]');
    if (stickyOriginal) { stickyOriginal.textContent = this.formatMoney(basePrice); stickyOriginal.hidden = !enabled; }
    if (stickySaving) { stickySaving.textContent = `${coupon.dataset.savingLabel || 'Saving'} ${this.formatMoney(saving)}`; stickySaving.hidden = !enabled; }
  }

  bindMemberModal() {
    const modal = document.querySelector('[data-member-modal]');
    if (!modal) return;
    const dialog = modal.querySelector('.member-modal__dialog');
    let returnFocus = null;
    const setOpen = (open) => {
      if (open) {
        returnFocus = document.activeElement;
        modal.hidden = false;
        document.body.classList.add('is-locked');
        dialog?.focus();
      } else {
        modal.hidden = true;
        if (!document.querySelector('.cart-drawer.is-open,.mobile-menu[open],.global-menu[open]')) document.body.classList.remove('is-locked');
        returnFocus?.focus?.();
      }
    };
    document.querySelectorAll('[data-member-open]').forEach((button) => button.addEventListener('click', () => setOpen(true)));
    modal.querySelectorAll('[data-member-close]').forEach((button) => button.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') setOpen(false);
      else this.trapFocus(dialog, event);
    });
  }

  bindDrawers() {
    const drawer = document.querySelector('.cart-drawer[data-cart-drawer]');
    const openers = document.querySelectorAll('[data-cart-open]');
    const closers = document.querySelectorAll('[data-cart-close]');
    let returnFocus = null;
    const setOpen = (open) => {
      if (!drawer) return;
      if (open) returnFocus = document.activeElement;
      drawer.classList.toggle('is-open', open);
      drawer.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('is-locked', open);
      if (open) drawer.querySelector('[data-cart-close]')?.focus();
      else returnFocus?.focus?.();
    };
    window.LestCartDrawer = { open: () => setOpen(true), close: () => setOpen(false) };
    openers.forEach((button) => button.addEventListener('click', (event) => {
      if (document.documentElement.dataset.cartDrawer !== 'true') return;
      event.preventDefault();
      setOpen(true);
    }));
    closers.forEach((button) => button.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      const openDrawer = drawer?.classList.contains('is-open');
      const openMenu = document.querySelector('.mobile-menu[open], .global-menu[open], .lest-account-menu[open]');
      if (event.key === 'Tab' && openDrawer) {
        this.trapFocus(drawer.querySelector('[role="dialog"]'), event);
        return;
      }
      if (event.key === 'Tab' && openMenu) {
        this.trapFocus(openMenu.querySelector('nav, [class*="panel"]') || openMenu, event);
        return;
      }
      if (event.key !== 'Escape') return;
      if (openDrawer) setOpen(false);
      const menuReturnFocus = openMenu?.querySelector(':scope > summary');
      document.querySelectorAll('.mobile-menu[open], .global-menu[open], .lest-account-menu[open]').forEach((menu) => menu.removeAttribute('open'));
      menuReturnFocus?.focus?.();
      if (!openDrawer) document.body.classList.remove('is-locked');
    });
    document.querySelectorAll('.mobile-menu, .global-menu').forEach((menu) => {
      menu.addEventListener('toggle', () => {
        const hasOpenOverlayMenu = Boolean(document.querySelector('.mobile-menu[open], .global-menu[open]'));
        document.body.classList.toggle('is-locked', hasOpenOverlayMenu);
      });
    });

    if (sessionStorage.getItem('lest-open-cart') === '1') {
      sessionStorage.removeItem('lest-open-cart');
      setOpen(true);
    }

    drawer?.querySelectorAll('[data-cart-change-key]').forEach((button) => button.addEventListener('click', async () => {
      await this.changeCartItem(button.dataset.cartChangeKey, Number(button.dataset.cartQuantity));
    }));
    drawer?.querySelectorAll('[data-cart-remove-key]').forEach((button) => button.addEventListener('click', async () => {
      await this.changeCartItem(button.dataset.cartRemoveKey, 0);
    }));
  }

  async changeCartItem(key, quantity) {
    if (!key || Number.isNaN(quantity)) return;
    document.querySelector('.cart-drawer[data-cart-drawer]')?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
      });
      if (!response.ok) throw new Error('Unable to update cart');
      sessionStorage.setItem('lest-open-cart', '1');
      window.location.reload();
    } catch (error) {
      document.querySelector('.cart-drawer[data-cart-drawer]')?.removeAttribute('aria-busy');
      console.error(error);
    }
  }

  bindTabs() {
    document.querySelectorAll('[data-tabs]').forEach((tabs) => {
      const buttons = [...tabs.querySelectorAll('[data-tab-button]')];
      const panels = [...tabs.querySelectorAll('[data-tab-panel]')];
      const activate = (button, focus = false) => {
        buttons.forEach((item) => item.classList.toggle('is-active', item === button));
        buttons.forEach((item) => item.setAttribute('aria-selected', String(item === button)));
        buttons.forEach((item) => item.tabIndex = item === button ? 0 : -1);
        panels.forEach((panel) => panel.hidden = panel.dataset.tabPanel !== button.dataset.tabButton);
        if (focus) button.focus();
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
          else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = buttons.length - 1;
          else return;
          event.preventDefault();
          activate(buttons[nextIndex], true);
        });
      });
    });
  }

  bindQuickAdd() {
    document.querySelectorAll('[data-quick-add], [data-product-form]').forEach((form) => form.addEventListener('submit', async (event) => {
      const isProductForm = form.hasAttribute('data-product-form');
      const action = event.submitter?.name;
      const coupon = isProductForm ? document.querySelector('[data-product-coupon]') : null;
      const couponEnabled = coupon?.querySelector('[data-coupon-toggle]')?.checked === true;
      if (isProductForm && action !== 'add' && action !== 'checkout') return;
      if (isProductForm && action === 'checkout' && !couponEnabled) return;
      event.preventDefault();
      if (form.dataset.submitting === 'true') return;
      form.dataset.submitting = 'true';
      const submit = event.submitter || form.querySelector('[type="submit"]');
      submit?.setAttribute('aria-busy', 'true');
      if (submit) submit.disabled = true;
      const previousStatus = form.querySelector('[data-quick-add-status]');
      if (previousStatus) previousStatus.textContent = '';
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Unable to add item');
        if (isProductForm && couponEnabled) {
          const root = window.Shopify.routes.root || '/';
          const redirect = action === 'checkout' ? `${root}checkout` : `${window.location.pathname}${window.location.search}`;
          if (action === 'add') sessionStorage.setItem('lest-open-cart', '1');
          window.location.assign(`${root}discount/${encodeURIComponent(coupon.dataset.code)}?redirect=${encodeURIComponent(redirect)}`);
        } else {
          sessionStorage.setItem('lest-open-cart', '1');
          window.location.reload();
        }
      } catch (error) {
        let status = form.querySelector('[data-quick-add-status]');
        if (!status) {
          status = document.createElement('p');
          status.setAttribute('data-quick-add-status', '');
          form.append(status);
        }
        status.classList.remove('visually-hidden');
        status.classList.add('product-form__error');
        status.setAttribute('role', 'alert');
        status.setAttribute('tabindex', '-1');
        status.textContent = document.body.dataset.cartAddError || 'Unable to add this item. Please try again.';
        status.focus();
      } finally {
        delete form.dataset.submitting;
        submit?.removeAttribute('aria-busy');
        if (submit) submit.disabled = false;
      }
    }));
  }

  bindSort() {
    document.querySelectorAll('[data-sort-by]').forEach((select) => select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
    }));
  }

  bindProductVariants() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const select = form.querySelector('[name="id"]');
      const productRoot = form.closest('[data-product-root]');
      const price = productRoot?.querySelector('[data-product-price]');
      let variantMedia = {};
      try {
        variantMedia = JSON.parse(productRoot?.querySelector('[data-variant-media-map]')?.textContent || '{}');
      } catch (_) {
        variantMedia = {};
      }
      const button = form.querySelector('[name="add"]');
      const updateVariant = () => {
        const option = select?.selectedOptions?.[0];
        const basePrice = Number(option?.dataset.priceCents || price?.dataset.basePrice || 0);
        this.applyCouponPrice(basePrice);
        if (button && option) {
          button.disabled = option.dataset.available !== 'true';
          button.textContent = option.dataset.available === 'true' ? button.dataset.addLabel : button.dataset.soldOutLabel;
        }
        if (select) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', select.value);
          history.replaceState({}, '', url);
        }
        const mediaId = select ? variantMedia[select.value] : null;
        const variantTitle = option?.textContent?.replace(/\s+[—-]\s+Sold out$/i, '').trim() || '';
        const detail = { mediaId, variantTitle };
        productRoot?.querySelector('[data-product-gallery]')?.dispatchEvent(new CustomEvent('lest:variant-media', { detail }));
        productRoot?.querySelector('[data-c4a-detail]')?.dispatchEvent(new CustomEvent('lest:variant-media', { detail }));
      };
      select?.addEventListener('change', updateVariant);
      updateVariant();
    });
  }

  bindProductExperience() {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
      const counter = gallery.querySelector('[data-gallery-current]');
      const total = gallery.querySelector('[data-gallery-total]');
      let active = 0;
      let visibleIndices = slides.map((_, index) => index);
      const show = (index) => {
        if (!slides.length) return;
        const requested = visibleIndices.includes(index) ? index : visibleIndices[0];
        active = requested ?? 0;
        slides.forEach((slide, itemIndex) => { slide.hidden = itemIndex !== active; slide.classList.toggle('is-active', itemIndex === active); });
        thumbs.forEach((thumb, itemIndex) => thumb.classList.toggle('is-active', itemIndex === active));
        const visiblePosition = Math.max(0, visibleIndices.indexOf(active));
        if (counter) counter.textContent = String(visiblePosition + 1);
        if (total) total.textContent = String(Math.max(1, visibleIndices.length));
      };
      const groupFromVariantTitle = (variantTitle) => {
        const title = String(variantTitle || '').toLowerCase();
        if (title.includes('magnolia white')) return 'magnolia-white';
        if (title.includes('avocado green')) return 'avocado-green';
        return '';
      };
      const setVariantGroup = (variantTitle) => {
        const group = groupFromVariantTitle(variantTitle);
        const hasGroupedMedia = group && slides.some((slide) => slide.dataset.mediaGroup === group);
        visibleIndices = slides
          .map((slide, index) => ({ slide, index }))
          .filter(({ slide }) => !hasGroupedMedia || slide.dataset.mediaGroup === group)
          .map(({ index }) => index);
        thumbs.forEach((thumb, index) => { thumb.hidden = !visibleIndices.includes(index); });
        show(visibleIndices.includes(active) ? active : visibleIndices[0]);
      };
      const showMediaById = (mediaId) => {
        if (!mediaId) return;
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index < 0) return;
        show(index);
        const thumb = thumbs[index];
        const strip = thumb?.parentElement;
        if (thumb && strip) {
          strip.scrollTo({ left: thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2, behavior: 'smooth' });
        }
      };
      const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const showMediaByVariantTitle = (variantTitle) => {
        const title = normalize(variantTitle);
        if (!title || title === 'default title') return;
        const titleWords = title.split(' ').filter((word) => word.length > 1);
        const index = slides.findIndex((slide) => {
          const mediaAlt = normalize(slide.dataset.mediaAlt);
          return mediaAlt && (mediaAlt.includes(title) || titleWords.every((word) => mediaAlt.includes(word)));
        });
        if (index >= 0) show(index);
      };
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => show(Number(thumb.dataset.galleryThumb))));
      const showPrevious = () => {
        const position = visibleIndices.indexOf(active);
        show(visibleIndices[(position - 1 + visibleIndices.length) % visibleIndices.length]);
      };
      const showNext = () => {
        const position = visibleIndices.indexOf(active);
        show(visibleIndices[(position + 1) % visibleIndices.length]);
      };
      gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', showPrevious);
      gallery.querySelector('[data-gallery-next]')?.addEventListener('click', showNext);
      gallery.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); showPrevious(); }
        if (event.key === 'ArrowRight') { event.preventDefault(); showNext(); }
      });
      let dragStart = null;
      gallery.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button') || event.pointerType === 'mouse' && event.button !== 0) return;
        dragStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        gallery.classList.add('is-dragging');
        gallery.setPointerCapture?.(event.pointerId);
      });
      const finishDrag = (event) => {
        if (!dragStart || event.pointerId !== dragStart.id) return;
        const deltaX = event.clientX - dragStart.x;
        const deltaY = event.clientY - dragStart.y;
        gallery.classList.remove('is-dragging');
        gallery.releasePointerCapture?.(event.pointerId);
        dragStart = null;
        if (Math.abs(deltaX) < 55 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        if (deltaX < 0) showNext(); else showPrevious();
      };
      gallery.addEventListener('pointerup', finishDrag);
      gallery.addEventListener('pointercancel', () => { dragStart = null; gallery.classList.remove('is-dragging'); });
      gallery.addEventListener('lest:variant-media', (event) => {
        setVariantGroup(event.detail?.variantTitle);
        if (event.detail?.mediaId) showMediaById(event.detail.mediaId);
        else showMediaByVariantTitle(event.detail?.variantTitle);
      });
    });

    document.querySelectorAll('[data-c4a-detail]').forEach((detail) => {
      const tabs = [...detail.querySelectorAll('[data-c4a-tab]')];
      const panels = [...detail.querySelectorAll('[data-c4a-panel]')];
      const productRoot = detail.closest('[data-product-root]');
      const select = productRoot?.querySelector('[data-product-form] [name="id"]');
      const groupFromTitle = (title) => String(title || '').toLowerCase().includes('avocado green') ? 'avocado-green' : 'magnolia-white';
      const showGroup = (group) => {
        tabs.forEach((tab) => {
          const selected = tab.dataset.c4aTab === group;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', String(selected));
        });
        panels.forEach((panel) => { panel.hidden = panel.dataset.c4aPanel !== group; });
        detail.dataset.selectedGroup = group;
      };
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        const targetTitle = tab.dataset.c4aVariantTitle;
        const option = [...(select?.options || [])].find((item) => item.textContent.toLowerCase().includes(targetTitle.toLowerCase()));
        if (select && option) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          showGroup(tab.dataset.c4aTab);
        }
      }));
      detail.addEventListener('lest:variant-media', (event) => showGroup(groupFromTitle(event.detail?.variantTitle)));
      showGroup(detail.dataset.selectedGroup || 'magnolia-white');
    });

    document.querySelectorAll('[data-feature-tabs]').forEach((tabs) => {
      const section = tabs.closest('.product-story');
      const buttons = [...tabs.querySelectorAll('[data-feature-button]')];
      const panels = [...section.querySelectorAll('[data-feature-panel]')];
      buttons.forEach((button) => button.addEventListener('click', () => {
        buttons.forEach((item) => item.classList.toggle('is-active', item === button));
        panels.forEach((panel) => { panel.hidden = panel.dataset.featurePanel !== button.dataset.featureButton; });
      }));
    });

    document.querySelectorAll('[data-reference-tabs]').forEach((tabs) => {
      const section = tabs.closest('.product-reference');
      const buttons = [...tabs.querySelectorAll('[data-reference-button]')];
      const panels = [...section.querySelectorAll('[data-reference-panel]')];
      buttons.forEach((button) => button.addEventListener('click', () => {
        buttons.forEach((item) => item.classList.toggle('is-active', item === button));
        panels.forEach((panel) => { panel.hidden = panel.dataset.referencePanel !== button.dataset.referenceButton; });
      }));
    });

    document.querySelectorAll('[data-quantity-minus],[data-quantity-plus]').forEach((button) => button.addEventListener('click', () => {
      const input = button.parentElement.querySelector('input[name="quantity"]');
      if (!input) return;
      input.value = String(Math.max(1, Number(input.value || 1) + (button.hasAttribute('data-quantity-plus') ? 1 : -1)));
    }));

    document.querySelectorAll('[data-product-coupon]').forEach((coupon) => {
      const toggle = coupon.querySelector('[data-coupon-toggle]');
      toggle?.addEventListener('change', () => {
        const basePrice = Number(document.querySelector('[data-product-price]')?.dataset.basePrice || 0);
        this.applyCouponPrice(basePrice);
      });
    });

    document.querySelectorAll('[data-copy-code]').forEach((button) => button.addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(button.dataset.copyCode); button.textContent = button.dataset.copiedLabel || 'Copied'; } catch (_) { button.textContent = button.dataset.copyCode; }
    }));

    document.querySelector('[data-sticky-add]')?.addEventListener('click', () => document.querySelector('[data-product-form] [name="add"]')?.click());
    document.querySelector('[data-sticky-buy]')?.addEventListener('click', () => {
      const buyButton = document.querySelector('[data-product-form] [name="checkout"]');
      if (buyButton) buyButton.click(); else document.querySelector('[data-product-form] [name="add"]')?.click();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new LestTheme());


/* Launch-readiness behavior */
document.addEventListener('DOMContentLoaded', () => {
  const translate17TrackFallback = () => {
    const isEnglish = document.documentElement.lang.toLowerCase().startsWith('en');
    const is17TrackPage = window.location.pathname.toLowerCase().includes('/apps/17track');
    const root = document.querySelector('.yq-app');
    if (!isEnglish || !is17TrackPage || !root) return;

    const translations = new Map([
      ['查询您的订单', 'Track Your Order'],
      ['订单编号', 'Order Number'],
      ['物流单号', 'Tracking Number'],
      ['邮箱', 'Email'],
      ['查询', 'Track']
    ]);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();
    while (textNode) {
      const source = textNode.nodeValue.trim();
      if (translations.has(source)) {
        textNode.nodeValue = textNode.nodeValue.replace(source, translations.get(source));
      }
      textNode = walker.nextNode();
    }
    root.querySelectorAll('input[placeholder], [aria-label], button[title]').forEach((element) => {
      ['placeholder', 'aria-label', 'title'].forEach((attribute) => {
        const source = element.getAttribute(attribute)?.trim();
        if (source && translations.has(source)) element.setAttribute(attribute, translations.get(source));
      });
    });
    root.querySelectorAll('button, [role="button"], input[type="submit"]').forEach((element) => {
      const source = (element.value || element.textContent || '').replace(/\s+/g, '');
      if (source !== '查询') return;
      if (element instanceof HTMLInputElement) element.value = 'Track';
      else element.textContent = 'Track';
    });
  };

  const normalizeCustomerHubEnglish = () => {
    const isEnglish = document.documentElement.lang.toLowerCase().startsWith('en');
    const isCustomerHub = window.location.pathname.toLowerCase().includes('/a/customerhub');
    if (!isEnglish || !isCustomerHub) return;

    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();
    while (textNode) {
      if (textNode.nodeValue.trim() === '中国') {
        textNode.nodeValue = textNode.nodeValue.replace('中国', 'China');
      }
      textNode = walker.nextNode();
    }

    document.querySelectorAll('input, option').forEach((element) => {
      if (element.value?.trim() === '中国') element.value = 'China';
      if (element.tagName === 'OPTION' && element.textContent.trim() === '中国') element.textContent = 'China';
    });

    document.querySelectorAll('input[type="date"]:not([data-lest-english-date])').forEach((input) => {
      input.dataset.lestEnglishDate = 'true';
      if (input.value) return;
      const showEnglishDatePlaceholder = () => {
        if (input.value) return;
        input.type = 'text';
        input.inputMode = 'numeric';
        input.placeholder = 'YYYY-MM-DD';
      };
      const restoreDatePicker = () => {
        input.type = 'date';
        input.removeAttribute('placeholder');
        try { input.showPicker?.(); } catch (_) { /* Browser controls picker availability. */ }
      };
      input.addEventListener('focus', restoreDatePicker);
      input.addEventListener('blur', showEnglishDatePlaceholder);
      showEnglishDatePlaceholder();
    });
  };

  let translationFrame;
  const schedule17TrackTranslation = () => {
    window.cancelAnimationFrame(translationFrame);
    translationFrame = window.requestAnimationFrame(translate17TrackFallback);
  };
  schedule17TrackTranslation();
  if (window.location.pathname.toLowerCase().includes('/apps/17track')) {
    const trackingObserver = new MutationObserver(schedule17TrackTranslation);
    trackingObserver.observe(document.body, { childList: true, subtree: true });
  }

  let customerHubFrame;
  const scheduleCustomerHubNormalization = () => {
    window.cancelAnimationFrame(customerHubFrame);
    customerHubFrame = window.requestAnimationFrame(normalizeCustomerHubEnglish);
  };
  scheduleCustomerHubNormalization();
  if (window.location.pathname.toLowerCase().includes('/a/customerhub')) {
    const customerHubObserver = new MutationObserver(scheduleCustomerHubNormalization);
    customerHubObserver.observe(document.body, { childList: true, subtree: true });
  }

  const sticky = document.querySelector('[data-product-sticky]');
  const primaryForm = document.querySelector('[data-product-form]');
  if (sticky && primaryForm) {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        const hasPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', hasPassed);
      }, { threshold: 0 });
      observer.observe(primaryForm);
    }
  }

  const hideMobileRewardsLauncher = () => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    document.querySelectorAll('#smile-ui-lite-launcher-frame-container, .smile-launcher-frame-container, [class*="smile-launcher"][class*="frame"]').forEach((node) => {
      node.setAttribute('hidden', '');
      node.setAttribute('aria-hidden', 'true');
    });
  };
  hideMobileRewardsLauncher();
  const rewardsObserver = new MutationObserver(hideMobileRewardsLauncher);
  rewardsObserver.observe(document.documentElement, { childList: true, subtree: true });
});

